import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import { Button } from "components/button";
import { Avatar } from "components/avatar";
import { useModalContext } from "components/modal";
import NewUserModal from "./container/newuser";
import { useHistory } from "react-router-dom";
import Table from "components/table";
import { useQuery } from "react-query";
import { RiAddLine } from "react-icons/ri";
import { FaChevronUp } from "react-icons/fa";
import { OwnerTag } from "components/tag/index";
import Switch from "rc-switch";
import { tohrs } from 'utils/tohrs';
import { useTranslation } from 'react-i18next';

const Con = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
`;

const Title = styled.div`
  text-align: left;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`;

const HeaderSection = styled.div`
  flex: none;
  background-color: #ffffff;
  z-index: 10;
  padding-bottom: 0.5rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 4rem;

  table thead th {
    position: sticky;
    top: 0;
    background-color: #ffffff;
    z-index: 5;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

export default function Userlist() {
  const { t } = useTranslation();
  const tBase = "views.admin.userlist";
  const tr = (key) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const history = useHistory();

  const [showInactive, setShowInactive] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const originalOverflow = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleScroll = (e) => {
    if (e.currentTarget.scrollTop > 100) {
      setShowScrollButton(true);
    } else {
      setShowScrollButton(false);
    }
  };

  const fetchData = useQuery(
    "users",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=ALLUSER`
        )
        .then((res) => res.data)
  );

  const StatusList = useQuery(
    "statlist",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/time_clock/statuses`
        )
        .then((res) => {
          return res.data;
        })
  );

  const columns = {
    name: {
      label: tr("Name"),
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar showLabel user_id={row?.user_id}>
            {row?.full_name}
          </Avatar>
          {row.owner && <OwnerTag>{tr("Owner")}</OwnerTag>}
          {row.status_inactive && (
            <span className="text-red-600 bg-red-100/40 p-1 text-xs rounded">
              {tr("Inactive")}
            </span>
          )}
        </div>
      ),
    },
    division: {
      label: tr("Division"),
    },
    job_title_label: {
      label: tr("Job Title"),
    },
    username: {
      label: tr("Username"),
    },
    pin: {
      label: tr("TIMEServ Pin"),
    },
    status: {
      label: tr("Status"),
      cell: (row) => {
        if (StatusList.data?.find((a) => a.user_id === row.user_id) === undefined) {
          return tr("No Status");
        }
        const status = StatusList.data?.find((a) => a.user_id === row.user_id)?.status;
        const translatedStatus =
          status === "Clocked-Out"
            ? tr("Clocked-Out")
            : status === "Clocked-In"
            ? tr("Clocked-In")
            : status === "On Break"
            ? tr("On Break")
            : status === "Lunch"
            ? tr("Lunch")
            : status === "Clocked-In-Idle"
            ? tr("Clocked-In-Idle")
            : status === "On-The-Way-Unassigned"
            ? tr("On-The-Way-Unassigned")
            : status === "In-Progress-Unassigned"
            ? tr("In-Progress-Unassigned")
            : status.includes("On-The-Way")
            ? `${tr("On-The-Way")}-${status.split("-").slice(2).join("-")}`
            : status.includes("In Progress")
            ? `${tr("In Progress")}-${status.split("-").slice(2).join("-")}`
            : status;
        return (
          translatedStatus +
          " (" +
          tohrs(
            StatusList.data?.find((a) => a.user_id === row.user_id)?.timeinstatus
          ) +
          ")"
        );
      },
    },
  };

  const handleClick = (row) => {
    if (row.user_id !== undefined) {
      history.push(`/admin/employees/${row.user_id}/permission/team`);
    } else {
      setModal({
        component: (
          <Button
            onClick={() => {
              setModal();
            }}
          >
            {tr("Okay")}
          </Button>
        ),
        label: tr("This Employee is not enabled as user on Eserv"),
      });
    }
  };

  return (
    <Con>
      <HeaderSection>
        <Title>
          <h1>{tr("Employees")}</h1>
          <Button
            primary
            style={{ marginLeft: "auto" }}
            onClick={() =>
              setModal({
                component: <NewUserModal />,
                label: tr("Add New User"),
                props: { shouldCloseOnOverlayClick: false },
              })
            }
          >
            <RiAddLine /> {tr("Add New User")}
          </Button>
        </Title>
        <ToggleContainer>
          <p style={{ margin: 0 }}>{tr("Show Inactive Employees")}</p>
          <Switch checked={showInactive} onChange={setShowInactive} />
        </ToggleContainer>
      </HeaderSection>

      <TableContainer ref={scrollContainerRef} onScroll={handleScroll}>
        <Table
          handleClick={handleClick}
          columns={columns}
          data={fetchData?.data
            ?.map((a) => {
              if (a.status_inactive === true && showInactive === false) {
                return null;
              }
              return a;
            })
            .filter((a) => {
              if (a === null) {
                return false;
              }
              return true;
            })}
        />
      </TableContainer>

      {showScrollButton && (
        <div className="fixed bottom-4 right-6 z-10">
          <button
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="bg-primary p-2 rounded-full shadow-md text-white hover:bg-primary-dark"
          >
            <FaChevronUp />
          </button>
        </div>
      )}
    </Con>
  );
}